import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';

const FerdiKazaSeo = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Insurance",
    "name": "Ferdi Kaza Sigortası",
    "description": "Beklenmedik kazalara karşı kendinizi ve sevdiklerinizi güvence altına alın. En uygun ferdi kaza sigortası tekliflerini hemen alın.",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "520"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Ferdi kaza sigortası nedir?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ferdi kaza sigortası, kaza sonucu oluşabilecek vefat, sürekli sakatlık ve tedavi giderlerini karşılayan bir sigorta türüdür. Poliçe kapsamında belirlenen teminatlar doğrultusunda, sigortalıya veya yakınlarına maddi güvence sağlar."
        }
      },
      {
        "@type": "Question",
        "name": "Ferdi kaza sigortası kimlere yapılabilir?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "16-64 yaş arasındaki herkes ferdi kaza sigortası yaptırabilir. 65 yaş üstü için özel şartlar geçerlidir."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Ferdi Kaza Sigortası Fiyatları ve Teminatları | SigortamOlsun</title>
        <meta name="description" content="Ferdi kaza sigortası ile beklenmedik kazalara karşı kendinizi ve sevdiklerinizi güvence altına alın. En uygun fiyatlar ve geniş teminatlar için hemen teklif alın!" />
        <meta name="keywords" content="ferdi kaza sigortası, kaza sigortası, vefat teminatı, sürekli sakatlık teminatı" />
        <link rel="canonical" href="https://sigortamolsun.com/ferdi-kaza-sigortasi" />
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="max-w-5xl mx-auto px-4 py-8 md:py-12">
        {/* Ana Başlık ve Açıklama */}
        <section className="mb-12">
          <h1 className="text-3xl font-bold mb-6">Ferdi Kaza Sigortası</h1>
          <div className="prose max-w-none">
            <p className="text-lg text-gray-600">
              Hayat beklenmedik sürprizlerle dolu olabilir. Ferdi kaza sigortası ile kendinizi ve sevdiklerinizi güvence altına alın. 
              Kaza sonucu oluşabilecek vefat, sürekli sakatlık ve tedavi giderleriniz için kapsamlı koruma sağlayın.
            </p>
          </div>
        </section>

        {/* Teminatlar */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Ferdi Kaza Sigortası Teminatları</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-semibold mb-3">Vefat Teminatı</h3>
              <p className="text-gray-600 text-sm">
                Kaza sonucu vefat durumunda, poliçede belirlenen teminat tutarı kanuni varislerinize ödenir. 
                Kaza tarihinden itibaren bir sene içinde gerçekleşen vefat durumları için geçerlidir.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-semibold mb-3">Sürekli Sakatlık Teminatı</h3>
              <p className="text-gray-600 text-sm">
                Kaza sonucu sürekli sakatlık durumunda, sakatlık oranına göre poliçede belirlenen teminat tutarı ödenir. 
                Kaza tarihinden itibaren iki sene içinde ortaya çıkan sakatlıklar için geçerlidir.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-semibold mb-3">Tedavi Giderleri Teminatı</h3>
              <p className="text-gray-600 text-sm">
                Kaza sonucu oluşan doktor ücreti, ilaç, radyografi, hastane ve diğer tedavi masraflarınız poliçe limitleri 
                dahilinde karşılanır.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-semibold mb-3">Gündelik Tazminat</h3>
              <p className="text-gray-600 text-sm">
                Kaza sonucu geçici iş göremezlik durumunda, poliçede belirtilen günlük tazminat 200 güne kadar ödenir. 
                Spor kazalarında 7 günü aşan süreler için geçerlidir.
              </p>
            </div>
          </div>
        </section>

        {/* Avantajlar */}
        <section className="mb-12 bg-gradient-to-br from-blue-50 to-purple-50 -mx-4 p-8">
          <h2 className="text-2xl font-bold mb-6">Ferdi Kaza Sigortası Avantajları</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-semibold mb-3">Fiyatları Çok Uygun</h3>
              <p className="text-gray-600 text-sm">
                Ferdi kaza sigortası primleri sağladığı teminata göre oldukça uygundur.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-semibold mb-3">Ücretsiz Ek Hizmetler</h3>
              <p className="text-gray-600 text-sm">
                Diş sağlığı paketi ve acil ambulans hizmetinden ücretsiz yararlanın.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-semibold mb-3">Otomatik Yenileme</h3>
              <p className="text-gray-600 text-sm">
                64 yaşına kadar poliçeniz otomatik olarak yenilenir.
              </p>
            </div>
          </div>
        </section>

        {/* Sigortalanabilir Kişiler */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Kimler Ferdi Kaza Sigortası Yaptırabilir?</h2>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="prose max-w-none">
              <h3 className="font-semibold mb-3">Meslek Gruplarına Göre Sınıflandırma</h3>
              <ul className="list-disc pl-5 space-y-2 text-gray-600">
                <li>Fikren veya elle çalışanlar</li>
                <li>Elle ve aynı zamanda bedenen çalışanlar</li>
                <li>Devamlı olarak makine ile bedenen veya elle çalışanlar</li>
              </ul>
              <p className="text-sm text-gray-500 mt-4">
                Not: 16-64 yaş arasındaki herkes ferdi kaza sigortası yaptırabilir. 65 yaş üstü için özel şartlar geçerlidir.
              </p>
            </div>
          </div>
        </section>

        {/* SSS */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Sıkça Sorulan Sorular</h2>
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-semibold mb-3">Hangi durumlar kaza sayılır?</h3>
              <ul className="list-disc pl-5 space-y-2 text-gray-600 text-sm">
                <li>Trafik kazaları (araç, yaya vb.)</li>
                <li>Ani gaz yayılması sonucu zehirlenmeler</li>
                <li>Yanıklar ve ani hareket sonucu yaralanmalar</li>
                <li>Yılan veya haşerat sokması sonucu zehirlenmeler</li>
                <li>Kuduz vakası sonucu oluşan durumlar</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-semibold mb-3">Ferdi kaza sigortası deprem hasarlarını karşılar mı?</h3>
              <p className="text-gray-600 text-sm">
                Evet, sigortalının deprem sonucu uğrayacağı zararlar teminat kapsamındadır.
              </p>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default FerdiKazaSeo;